import chatService from '@/api/services/chat'
import { ref, readonly, computed, onUnmounted, triggerRef } from 'vue'

// Shared state - defined OUTSIDE the composable so it's shared across all components
const messages = ref([])
const currentSession = ref(null)
const sessions = ref([])
const isConnected = ref(true) // Always "connected" with polling
const isConnecting = ref(false)
const isThinking = ref(false)
const thinkingSteps = ref([])
const userReports = ref([])
const loading = ref(false)
const error = ref(null)

// Polling state
const currentJobId = ref(null)
const pollingInterval = ref(null)
const currentSessionId = ref(null)
const pollingStartTime = ref(null)
const pendingPlan = ref(null)
// Answer text as it streams, and when the current turn started — both shown
// while waiting so a long answer does not look like a hung one.
const partialAnswer = ref('')
const turnStartedAt = ref(null)
// Set when polling gave up but the answer may still land: the session is
// re-checked in the background and the notice clears when it arrives.
const recoveringAnswer = ref(false)
// True when the last question never got an answer and can be re-run as is.
const canRetry = ref(false)
let pollInFlight = false
let notFoundSince = null
let recoveryTimer = null
const POLL_INTERVAL_MS = 500
// Deep multi-tool runs regularly exceed 3 minutes; the backend keeps working
// after the FE gives up, so a short cap just hides the answer.
const POLL_TIMEOUT_MS = 600000 // 10 minutes max polling time
// Job state is shared across backend instances, so a 404 should be rare (a job
// swept or deleted). Still only treat "not found" as final once it persists.
const NOT_FOUND_GRACE_MS = 30000
const RECOVERY_INTERVAL_MS = 15000
const RECOVERY_WINDOW_MS = 10 * 60000

// Axios errors reach us already unwrapped to the response by api/response.js,
// so the status is on the object itself; keep the raw-axios shape as fallback.
const errorStatus = (err) => err?.status ?? err?.response?.status
const errorDetail = (err) => err?.data?.detail || err?.response?.data?.detail || err?.message

export const useChat = () => {

  // Helper function to check if a value is empty/null
  const isEmpty = (value) => {
    return value === null || value === undefined || value === ''
  }

  // Helper function to find a matching report dynamically
  // Reports match if all non-empty fields from the new report match the existing report
  const findMatchingReport = (newReport) => {
    console.log('findMatchingReport', userReports.value)
    return userReports.value.findIndex((existingReport) => {
      console.log('existingReport', existingReport)
      console.log('newReport', newReport)
      // Company is always required
      if (existingReport.company !== newReport.company) {
        return false
      }

      // For other fields, match if either both are empty or both have the same value
      const fieldsToMatch = ['exposureType', 'reportType', 'eventTitle']

      for (const field of fieldsToMatch) {
        const existingValue = existingReport[field]
        const newValue = newReport[field]

        // If both are empty, that's a match for this field
        if (isEmpty(existingValue) && isEmpty(newValue)) {
          continue
        }

        // If one is empty and the other isn't, they can still match (dynamic matching)
        if (isEmpty(existingValue) || isEmpty(newValue)) {
          continue
        }

        // If both have values, they must match
        if (existingValue !== newValue) {
          return false
        }
      }

      return true
    })
  }

  // Create a new chat session
  const createSession = async (options = {}) => {
    try {
      loading.value = true
      error.value = null

      const token = localStorage.getItem('token')
      if (!token) {
        error.value = 'No authentication token found'
        return null
      }

      const response = await chatService.createSession(options)

      currentSession.value = response
      messages.value = [] // Clear messages for new session
      await loadSessions() // Refresh sessions list
      return response
    } catch (err) {
      error.value = err.message || 'Failed to create session'
      return null
    } finally {
      loading.value = false
    }
  }

  // Load all sessions
  const loadSessions = async () => {
    try {
      const token = localStorage.getItem('token')
      if (!token) {
        sessions.value = []
        return
      }

      const response = await chatService.loadSessions()
      sessions.value = response
    } catch (err) {
      console.error('Failed to load sessions:', err)
    }
  }

  // Delete a session
  const deleteSession = async (sessionId) => {
    try {
      const token = localStorage.getItem('token')
      if (!token) {
        throw new Error('No authentication token found')
      }

      await chatService.deleteSession(sessionId)

      // Remove from sessions list
      sessions.value = sessions.value.filter(s => s.id !== sessionId)

      // If we deleted the current session, clear it
      if (currentSession.value && currentSession.value.id === sessionId) {
        currentSession.value = null
        messages.value = []
      }

      return true
    } catch (err) {
      console.error('Failed to delete session:', err)
      throw err
    }
  }

  // Extract create_document artifacts from a message's tools_trace so
  // download chips survive a page reload. Returns null when there are none.
  const extractArtifactsFromTrace = (toolsTrace) => {
    if (!toolsTrace) return null
    const artifacts = []
    try {
      const toolTraces = JSON.parse(toolsTrace)
      toolTraces.forEach((toolMessage) => {
        if (toolMessage.role === 'tool' && toolMessage.content) {
          try {
            const toolContent = JSON.parse(toolMessage.content)
            if (toolContent.tool_name === 'create_document' &&
                toolContent.status === 'created' &&
                toolContent.artifact_id != null) {
              artifacts.push({
                artifact_id: toolContent.artifact_id,
                filename: toolContent.filename,
                size_bytes: toolContent.size_bytes
              })
            }
          } catch (parseErr) {
            // Non-JSON content, skip
          }
        }
      })
    } catch (traceErr) {
      // Invalid tools_trace, skip
    }
    return artifacts.length > 0 ? artifacts : null
  }

  // Load a session with its messages
  const loadSession = async (sessionId) => {
    try {
      loading.value = true
      error.value = null
      canRetry.value = false
      currentSessionId.value = sessionId

      const token = localStorage.getItem('token')
      if (!token) {
        error.value = 'No authentication token found'
        return
      }

      const response = await chatService.loadSession(sessionId)

      currentSession.value = {
        id: response.id,
        title: response.title,
        user_id: response.user_id,
        username: response.username,
        created_at: response.created_at,
        updated_at: response.updated_at
      }

      // Convert database messages to chat messages
      messages.value = (response.messages || []).map((msg, index) => {
        // Use new sources array if available, otherwise fall back to tools_trace extraction
        let sources = null

        if (msg.sources && Array.isArray(msg.sources) && msg.sources.length > 0) {
          // New format from backend - filter out internal tools
          const internalTools = ['write_final_answer', 'final_answer', 'respond_to_user']
          sources = msg.sources.filter(s => !internalTools.includes(s.type))
          if (sources.length === 0) sources = null
        } else if (msg.tools_trace) {
          // Legacy: Extract sources from tools_trace
          const extractedSources = []
          const internalToolsLegacy = ['write_final_answer', 'final_answer', 'respond_to_user']
          try {
            const toolTraces = JSON.parse(msg.tools_trace)
            toolTraces.forEach((toolMessage) => {
              if (toolMessage.role === 'tool' && toolMessage.content) {
                try {
                  const toolContent = JSON.parse(toolMessage.content)
                  // Filter out internal tools
                  if (toolContent.tool_name && !internalToolsLegacy.includes(toolContent.tool_name)) {
                    extractedSources.push({
                      type: toolContent.tool_name,
                      title: toolContent.tool_parameters?.company_name ||
                             toolContent.tool_parameters?.report_type ||
                             toolContent.tool_name,
                      domain: 'NVIYA'
                    })
                  }
                } catch (parseErr) {
                  // Non-JSON content, skip
                }
              }
            })
          } catch (traceErr) {
            // Invalid tools_trace, skip
          }
          if (extractedSources.length > 0) {
            sources = extractedSources
          }
        }

        // Extract citations if present
        const citations = (msg.citations && Array.isArray(msg.citations) && msg.citations.length > 0)
          ? msg.citations
          : null

        return {
          id: msg.id || index,
          role: msg.type,
          content: msg.content,
          messageType: msg.messageType || 'text',
          audioData: msg.audioData || null,
          timestamp: new Date(msg.created_at),
          sources,
          citations,
          artifacts: extractArtifactsFromTrace(msg.tools_trace)
        }
      })

      // Parse tool traces and extract reports
      if (response.messages) {
        response.messages.forEach((msg) => {
          if (msg.tools_trace) {
            try {
              const toolTraces = JSON.parse(msg.tools_trace)
              console.log('toolTraces', toolTraces)
              toolTraces.forEach((toolMessage) => {
                if (toolMessage.role === 'tool' && toolMessage.content) {
                  try {
                    const toolContent = JSON.parse(toolMessage.content)
                    if (toolContent.tool_name === 'report_selector' && toolContent.user_facing_report) {
                      // Handle source field if present
                      let reportContent = toolContent.user_facing_report

                      if (toolContent.source && toolContent.source !== 'none') {
                        try {
                          const sources = JSON.parse(toolContent.source)
                          if (Array.isArray(sources) && sources.length > 0) {
                            reportContent += '\n\nSources:\n'
                            sources.forEach((source, index) => {
                              reportContent += `${index + 1}. ${source.title}`
                              if (source.url !== 'none') {
                                reportContent += `\n   ${source.url}`
                              }
                              reportContent += '\n'
                            })
                          }
                        } catch (sourceError) {
                          console.warn('Failed to parse source field in loadSession:', sourceError)
                        }
                      }

                      // Extract report information from tool message
                      const reportData = {
                        id: Date.now(),
                        company: toolContent.tool_parameters?.company_name,
                        exposureType: toolContent.tool_parameters?.exposure_type || null,
                        eventTitle: toolContent.tool_parameters?.event_title || null,
                        reportType: toolContent.tool_parameters?.report_type,
                        content: reportContent,
                        timestamp: new Date(msg.created_at)
                      }

                      // Only add if company is present (minimum required field)
                      if (reportData.company && reportData.content) {
                        // Use dynamic matching to find existing report
                        const existingReportIndex = findMatchingReport(reportData)

                        if (existingReportIndex === -1) {
                          // No existing report found, add the new one
                          userReports.value.push(reportData)
                        }
                        // If a matching report exists, keep it and skip the new one
                      }
                    }
                  } catch (parseError) {
                    console.warn('Failed to parse tool content:', parseError)
                  }
                }
              })
            } catch (traceError) {
              console.warn('Failed to parse tools_trace:', traceError)
            }
          }
        })
      }

    } catch (err) {
      error.value = err.message || 'Failed to load session'
    } finally {
      loading.value = false
      // Check for pending message after loading session (runs even if parsing threw)
      await handlePendingMessage()
    }
  }

  // Handle pending message from localStorage (used when navigating from index page)
  const handlePendingMessage = async () => {
    try {
      const pendingMessage = localStorage.getItem('pendingChatMessage')
      const pendingAudioData = localStorage.getItem('pendingAudioData')

      if (pendingMessage && pendingMessage.trim() && currentSessionId.value) {
        console.log('Found pendingChatMessage; sending now')
        // Remove first to avoid any duplicate sends
        localStorage.removeItem('pendingChatMessage')

        // Check if it's an audio message
        if (pendingMessage === '[Audio Message]' && pendingAudioData) {
          console.log('Sending pending audio message')
          localStorage.removeItem('pendingAudioData')
          await sendMessage('[Audio Message]', 'audio', pendingAudioData)
        } else {
          // Send text message
          await sendMessage(pendingMessage)
        }
      }
    } catch (e) {
      console.warn('Unable to read/send pendingChatMessage:', e)
    }
  }

  // Stop polling
  const stopPolling = () => {
    if (pollingInterval.value) {
      clearInterval(pollingInterval.value)
      pollingInterval.value = null
    }
    currentJobId.value = null
    pollingStartTime.value = null
    notFoundSince = null
  }

  const stopRecovery = () => {
    if (recoveryTimer) {
      clearInterval(recoveryTimer)
      recoveryTimer = null
    }
    recoveringAnswer.value = false
  }

  // The backend saves the assistant message to the session whether or not we
  // are still polling. Re-read the session; true when an answer to the last
  // user message is there.
  const answerLandedInSession = async (sessionId) => {
    const session = await chatService.loadSession(sessionId)
    const stored = session?.messages || []
    const last = stored[stored.length - 1]
    // A text turn has its user message optimistically in `messages` already,
    // an audio turn does not — either way a landed answer makes the stored
    // list longer than what is on screen.
    return Boolean(last && last.type === 'assistant' && stored.length > messages.value.length)
  }

  // Polling could not finish the turn (timed out, or the job is unreachable).
  // Keep checking the session quietly instead of declaring failure.
  const startRecovery = (sessionId) => {
    stopRecovery()
    if (!sessionId) return
    recoveringAnswer.value = true
    isThinking.value = false
    thinkingSteps.value = []
    const deadline = Date.now() + RECOVERY_WINDOW_MS
    const check = async () => {
      if (currentSessionId.value !== sessionId) return stopRecovery()
      try {
        if (await answerLandedInSession(sessionId)) {
          stopRecovery()
          partialAnswer.value = ''
          error.value = null
          await loadSession(sessionId)
          return
        }
      } catch (e) {
        console.warn('[Recovery] session check failed:', e)
      }
      if (Date.now() > deadline) {
        stopRecovery()
        error.value = "We couldn't get this answer back."
        canRetry.value = true
      }
    }
    check()
    recoveryTimer = setInterval(check, RECOVERY_INTERVAL_MS)
  }

  // Poll for job status
  const pollForJobStatus = async (jobId) => {
    // Check for timeout
    if (pollingStartTime.value && Date.now() - pollingStartTime.value > POLL_TIMEOUT_MS) {
      console.warn('[Polling] Timeout reached, switching to session recovery')
      const sessionId = currentSessionId.value
      stopPolling()
      startRecovery(sessionId)
      return
    }

    // A poll slower than the interval must not overlap the next one — two
    // overlapping polls that both see "completed" appended the answer twice.
    if (pollInFlight) return
    pollInFlight = true

    try {
      const response = await chatService.pollJob(jobId)
      // A poll that resolves after the turn ended (completed, cancelled or a
      // newer message) must not touch the UI.
      if (currentJobId.value !== jobId) return
      notFoundSince = null

      if (response.status === 'running' || response.status === 'pending') {
        partialAnswer.value = response.partial_answer || ''
      }


      // Update thinking steps if present
      if (response.thinking_steps && Array.isArray(response.thinking_steps)) {
        // Convert to our internal format
        const rawSteps = response.thinking_steps.map((step, index) => {
          // Backend wraps step data inside content: { step_type, content: {...}, timestamp }
          const c = step.content || step
          // Free-text plans (e.g. "Searching Tesla's exposure data") contain spaces;
          // known step types (e.g. "retrieving_resources") are snake_case
          const isFreeText = typeof c.step === 'string' && c.step.includes(' ')
          return {
            ...c,
            step: c.step || index + 1,
            tool: c.tool || (isFreeText ? 'intent_plan' : c.step),
            status: c.status || 'in_progress',
            summary: c.message || c.summary || c.plan || (isFreeText ? c.step : undefined),
            timestamp: new Date(step.timestamp || c.timestamp || Date.now()),
            id: c.step_id || c.id || Date.now() + index
          }
        })

        // Collapse steps that share the same step_id — keep the latest (completed overwrites started)
        const stepMap = new Map()
        for (const step of rawSteps) {
          if (step.id && stepMap.has(step.id)) {
            // Merge: later step wins for status/summary, preserve earlier data as fallback
            const existing = stepMap.get(step.id)
            stepMap.set(step.id, { ...existing, ...step })
          } else {
            stepMap.set(step.id, step)
          }
        }
        thinkingSteps.value = Array.from(stepMap.values())
        triggerRef(thinkingSteps)
      }

      // Handle user-facing reports
      if (response.user_facing_report) {
        handleUserFacingReport(response.user_facing_report)
      }

      // Handle session title update
      if (response.title) {
        if (currentSession.value) {
          currentSession.value = {
            ...currentSession.value,
            title: response.title,
            updated_at: new Date().toISOString()
          }
        }
        // Update in sessions list
        const sessionIndex = sessions.value.findIndex(s => s.id === currentSessionId.value)
        if (sessionIndex !== -1) {
          sessions.value[sessionIndex] = {
            ...sessions.value[sessionIndex],
            title: response.title,
            updated_at: new Date().toISOString()
          }
        }
      }

      // Handle plan confirmation prompt
      if (response.status === 'awaiting_confirmation' && response.plan) {
        pendingPlan.value = {
          planId: response.plan_id,
          summary: response.plan.summary,
          steps: response.plan.steps || []
        }
        // The clock is the user's now, not the backend's — keep pushing the
        // poll deadline while the plan card waits so we don't fake-timeout.
        pollingStartTime.value = Date.now()
      } else if (pendingPlan.value && response.status !== 'awaiting_confirmation') {
        // Plan was responded to, clear it
        pendingPlan.value = null
      }

      // Check if job is completed, failed, or cancelled
      if (response.status === 'completed') {
        handleJobCompleted(response)
        stopPolling()

        // Clean up the job
        try {
          await chatService.cleanupJob(jobId)
        } catch (cleanupErr) {
          console.warn('Failed to cleanup job:', cleanupErr)
        }
      } else if (response.status === 'cancelled') {
        handleJobCancelled()
        stopPolling()

        // Clean up the job
        try {
          await chatService.cleanupJob(jobId)
        } catch (cleanupErr) {
          console.warn('Failed to cleanup job:', cleanupErr)
        }
      } else if (response.status === 'failed') {
        handleJobFailed(response)
        stopPolling()

        // Clean up the job
        try {
          await chatService.cleanupJob(jobId)
        } catch (cleanupErr) {
          console.warn('Failed to cleanup job:', cleanupErr)
        }
      }
    } catch (err) {
      console.error('Polling error:', err)
      // Don't stop polling on transient errors. A 404 is usually a poll routed
      // to a backend instance that isn't running the job — keep trying for a
      // while, then fall back to watching the session for the saved answer.
      if (errorStatus(err) === 404 && currentJobId.value === jobId) {
        if (!notFoundSince) notFoundSince = Date.now()
        if (Date.now() - notFoundSince > NOT_FOUND_GRACE_MS) {
          const sessionId = currentSessionId.value
          stopPolling()
          startRecovery(sessionId)
        }
      }
    } finally {
      pollInFlight = false
    }
  }

  // Handle completed job
  const handleJobCompleted = (response) => {
    console.log('[Polling] Job completed:', response)
    console.log('[Polling] Setting isThinking to false')

    isThinking.value = false
    triggerRef(isThinking)
    partialAnswer.value = ''
    turnStartedAt.value = null
    console.log('[Polling] isThinking is now:', isThinking.value)

    // Build the final message
    const finalMessage = {
      id: response.message_id || Date.now(),
      role: 'assistant',
      content: response.final_answer || '',
      messageType: 'text',
      audioData: response.audio_data || null,
      timestamp: new Date()
    }

    // Process sources
    const internalTools = ['write_final_answer', 'final_answer', 'respond_to_user']
    if (response.sources && Array.isArray(response.sources) && response.sources.length > 0) {
      finalMessage.sources = response.sources.filter(s => !internalTools.includes(s.type))
      if (finalMessage.sources.length === 0) finalMessage.sources = undefined
    } else if (thinkingSteps.value.length > 0) {
      // Extract from thinking steps (filter out internal tools)
      const extractedSources = thinkingSteps.value
        .filter(step => step.tool && step.status === 'completed' && !internalTools.includes(step.tool))
        .map(step => ({
          type: step.tool,
          title: step.summary || step.tool,
          domain: 'NVIYA'
        }))
      if (extractedSources.length > 0) {
        finalMessage.sources = extractedSources
      }
    }

    // Attach thinking steps
    if (thinkingSteps.value.length > 0) {
      finalMessage.thinkingSteps = [...thinkingSteps.value]
    }

    // Add citations (inline source references)
    if (response.citations && Array.isArray(response.citations) && response.citations.length > 0) {
      finalMessage.citations = response.citations
    }

    // Add followup suggestions
    if (response.followup_suggestions) {
      console.log('[Polling] Adding followup suggestions:', response.followup_suggestions)
      finalMessage.followupSuggestions = response.followup_suggestions
    }

    // Add generated document artifacts (create_document tool) — download chips
    if (response.artifacts && Array.isArray(response.artifacts) && response.artifacts.length > 0) {
      finalMessage.artifacts = response.artifacts
    }

    console.log('[Polling] Final message:', finalMessage)

    messages.value = [...messages.value, finalMessage]
    triggerRef(messages)

    // Clear thinking steps
    thinkingSteps.value = []
  }

  // Handle cancelled job
  const handleJobCancelled = () => {
    console.log('[Polling] Job cancelled')
    isThinking.value = false
    thinkingSteps.value = []
    partialAnswer.value = ''
    turnStartedAt.value = null
  }

  // Handle failed job
  const handleJobFailed = (response) => {
    console.error('[Polling] Job failed:', response)

    isThinking.value = false
    thinkingSteps.value = []
    partialAnswer.value = ''
    turnStartedAt.value = null
    // response.error is a raw exception string — log it, don't show it. The
    // backend's own interrupted-turn message is written for people, so keep it.
    error.value = response.error?.startsWith('This answer was interrupted')
      ? 'This answer was interrupted before it finished.'
      : 'Something went wrong while answering.'
    canRetry.value = true
  }

  // Handle user-facing report from response
  const handleUserFacingReport = (reportData) => {
    let reportContent = reportData.content

    // Handle source field if present and not "none"
    if (reportData.source && reportData.source !== 'none') {
      try {
        const sources = JSON.parse(reportData.source)
        if (Array.isArray(sources) && sources.length > 0) {
          reportContent += '\n\nSources:\n'
          sources.forEach((source, index) => {
            reportContent += `${index + 1}. ${source.title}\n   ${source.url}\n`
          })
        }
      } catch (sourceError) {
        console.warn('Failed to parse source field:', sourceError)
      }
    }

    const report = {
      id: Date.now(),
      company: reportData.company,
      exposureType: reportData.exposure_type || null,
      eventTitle: reportData.event_title || null,
      reportType: reportData.report_type,
      content: reportContent,
      timestamp: new Date()
    }

    // Ensure company is present (minimum required field)
    if (!report.company) {
      console.warn('Received user_facing_report with missing company field')
      return
    }

    console.log('on new message report', report)
    // Use dynamic matching to find existing report
    const existingReportIndex = findMatchingReport(report)

    if (existingReportIndex === -1) {
      // No existing report found, add the new one
      userReports.value.push(report)
    }
  }

  // Send a message
  const sendMessage = async (content, messageType = 'text', audioData = null) => {
    console.log('[sendMessage] Called with:', { content: content?.substring(0, 50), messageType })

    if (!currentSessionId.value) {
      error.value = 'No session selected'
      return
    }

    if (messageType === 'text' && !content.trim()) return
    if (messageType === 'audio' && !audioData) return

    // Add user message to UI immediately (optimistic update) - but skip for audio messages
    let userMessage = null
    if (messageType !== 'audio') {
      userMessage = {
        id: Date.now(), // Temporary ID
        role: 'user',
        content: content.trim(),
        messageType: messageType,
        audioData: audioData,
        timestamp: new Date()
      }
      messages.value = [...messages.value, userMessage]
      console.log('[sendMessage] Added optimistic user message, total messages:', messages.value.length)
      triggerRef(messages)
    }

    // Set thinking state
    stopRecovery()
    canRetry.value = false
    isThinking.value = true
    error.value = null
    partialAnswer.value = ''
    thinkingSteps.value = []
    turnStartedAt.value = Date.now()

    try {
      // Submit message to server
      const response = await chatService.sendMessage(
        currentSessionId.value,
        messageType === 'audio' ? '[Audio Message]' : content.trim(),
        messageType,
        audioData
      )

      console.log('[sendMessage] Got job_id:', response.job_id)
      currentJobId.value = response.job_id

      // Update user message with real ID if returned
      if (response.user_message_id && userMessage) {
        const msgIndex = messages.value.findIndex(m => m.id === userMessage.id)
        if (msgIndex !== -1) {
          messages.value.splice(msgIndex, 1, {
            ...messages.value[msgIndex],
            id: response.user_message_id
          })
        }
      }

      await startPollingJob(response.job_id)

    } catch (err) {
      console.error('Failed to send message:', err)
      error.value = errorDetail(err) || 'Failed to send message'
      isThinking.value = false
      turnStartedAt.value = null

      // Remove the optimistic message on error
      if (userMessage) {
        messages.value = messages.value.filter(m => m.id !== userMessage.id)
      }
    }
  }

  const startPollingJob = async (jobId) => {
    pollingStartTime.value = Date.now()
    pollingInterval.value = setInterval(() => {
      pollForJobStatus(jobId)
    }, POLL_INTERVAL_MS)
    // Also poll immediately
    await pollForJobStatus(jobId)
  }

  // Re-run the last question after a failed or interrupted turn. The backend
  // takes the question from the saved chat, so nothing is retyped or duplicated.
  const retryLastMessage = async () => {
    if (!currentSessionId.value || isThinking.value) return
    stopPolling()
    stopRecovery()
    canRetry.value = false
    error.value = null
    partialAnswer.value = ''
    thinkingSteps.value = []
    isThinking.value = true
    turnStartedAt.value = Date.now()

    try {
      const response = await chatService.retryLastMessage(currentSessionId.value)
      currentJobId.value = response.job_id
      await startPollingJob(response.job_id)
    } catch (err) {
      console.error('Failed to retry:', err)
      isThinking.value = false
      turnStartedAt.value = null
      if (errorStatus(err) === 409) {
        // The answer landed after all — show it rather than an error.
        await loadSession(currentSessionId.value)
      } else {
        error.value = errorDetail(err) || 'Could not retry. Please ask again.'
        canRetry.value = true
      }
    }
  }

  // Respond to a pending plan (approve or reject)
  const respondToPlan = async (action) => {
    if (!currentJobId.value || !pendingPlan.value) return

    const planId = pendingPlan.value.planId
    console.log('[Plan] Responding with:', { planId, action })

    try {
      await chatService.respondToPlan(currentJobId.value, planId, action)
      pendingPlan.value = null
      pollingStartTime.value = Date.now() // Reset timeout — new phase of work
    } catch (err) {
      console.error('Failed to respond to plan:', err)
      if (errorStatus(err) === 409) {
        // The plan is no longer waiting (it expired after 5 minutes). The next
        // poll delivers the backend's explanation, so just drop the card.
        pendingPlan.value = null
      } else {
        error.value = 'Failed to send plan response'
      }
    }
  }

  // Cancel the current in-progress message
  const cancelMessage = async () => {
    if (!currentJobId.value) return

    const jobId = currentJobId.value
    stopPolling()
    isThinking.value = false
    thinkingSteps.value = []
    pendingPlan.value = null
    partialAnswer.value = ''
    turnStartedAt.value = null

    try {
      await chatService.cancelJob(jobId)
    } catch (err) {
      console.warn('Failed to cancel job:', err)
    }
  }

  // Connect to session (for API compatibility - just stores the session ID)
  const connectWebSocket = (sessionId) => {
    currentSessionId.value = sessionId
    isConnected.value = true

    // Handle any pending messages
    handlePendingMessage()
  }

  // Disconnect (stop polling)
  const disconnect = () => {
    stopPolling()
    stopRecovery()
    canRetry.value = false
    currentSessionId.value = null
    isThinking.value = false
    thinkingSteps.value = []
    partialAnswer.value = ''
    turnStartedAt.value = null
  }

  // Clean up on unmount
  onUnmounted(() => {
    disconnect()
  })

  return {
    // State - using refs directly for arrays that need reactivity triggers
    messages,
    currentSession,
    sessions,
    isConnected: readonly(isConnected),
    isConnecting: readonly(isConnecting),
    isReconnecting: computed(() => false), // Not applicable for polling
    isThinking,
    thinkingSteps,
    pendingPlan,
    partialAnswer: readonly(partialAnswer),
    turnStartedAt: readonly(turnStartedAt),
    recoveringAnswer: readonly(recoveringAnswer),
    canRetry: readonly(canRetry),
    retryLastMessage,
    userReports,
    loading: readonly(loading),
    error,
    ws: computed(() => null), // For backwards compatibility

    // Actions
    createSession,
    loadSessions,
    loadSession,
    deleteSession,
    connectWebSocket,
    sendMessage,
    cancelMessage,
    respondToPlan,
    disconnect,

    // Manual report management
    addReport: (report) => {
      // Ensure required fields are present
      if (!report.company || !report.reportType) {
        console.warn('Report must have company and reportType to be added')
        return
      }

      // Handle source field if present
      let reportContent = report.content
      if (report.source && report.source !== 'none') {
        try {
          const sources = typeof report.source === 'string' ? JSON.parse(report.source) : report.source
          if (Array.isArray(sources) && sources.length > 0) {
            reportContent += '\n\nSources:\n'
            sources.forEach((source, index) => {
              reportContent += `${index + 1}. ${source.title}\n   ${source.url}\n`
            })
          }
        } catch (sourceError) {
          console.warn('Failed to parse source field in addReport:', sourceError)
        }
      }

      const newReport = {
        id: report.id || Date.now(),
        company: report.company,
        exposureType: report.exposureType || null,
        eventTitle: report.eventTitle || null,
        reportType: report.reportType || null,
        content: reportContent,
        timestamp: report.timestamp || new Date()
      }

      // Use dynamic matching to find existing report
      const existingReportIndex = findMatchingReport(newReport)

      if (existingReportIndex === -1) {
        // No existing report found, add the new one
        userReports.value.push(newReport)
      }
      // If a matching report exists, keep it and skip the new one
    },

    removeReport: (reportId) => {
      const index = userReports.value.findIndex(r => r.id === reportId)
      if (index !== -1) {
        userReports.value.splice(index, 1)
      }
    },

    clearReports: () => {
      userReports.value = []
    },

    // Computed
    hasMessages: computed(() => messages.value.length > 0)
  }
}
